import { CATEGORIES, HANDLER_TYPES, INTERACTION_TYPES, TRIGGERS } from 'craft/types';

export function generateEventName(prefix: string): string {
  return `${prefix} Clicked`;
}

export interface Interaction {
  id: INTERACTION_TYPES;
  category: CATEGORIES;
  name: string;
  defaultAction: () => void;
}

export const interactions: Interaction[] = [
  {
    id: INTERACTION_TYPES.None,
    category: CATEGORIES.NATIVE,
    name: 'None',
    defaultAction: () => null,
  },
  // we put this interaction first so that whenever you have are in a tour when you select an action it will default to it.
  {
    id: INTERACTION_TYPES.Tour,
    category: CATEGORIES.TOUR,
    name: 'Go to Tour Step',
    defaultAction: () => ({
      category: CATEGORIES.TOUR,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.NEXT_TOUR_STEP,
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.DismissTour,
    category: CATEGORIES.NATIVE,
    name: 'Dismiss Tour',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.DISMISS_TOUR,
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.DismissHotspotGroup,
    category: CATEGORIES.NATIVE,
    name: 'Dismiss Hotspot Group',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.DISMISS_HOTSPOT_GROUP,
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.DismissAndOpenNextHotspot,
    category: CATEGORIES.NATIVE,
    name: 'Dismiss & Open Next Hotspot',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.DISMISS_AND_OPEN_NEXT_HOTSPOT,
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.DismissHotspot,
    category: CATEGORIES.NATIVE,
    name: 'Dismiss Hotspot',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.DISMISS_HOTSPOT,
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.DismissModal,
    category: CATEGORIES.NATIVE,
    name: 'Dismiss Overlay',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.DISMISS_MODAL,
        void: '',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  // we put this interaction first so that whenever you have an open flow when you select an action it will default to it.
  {
    id: INTERACTION_TYPES.Flow,
    category: CATEGORIES.FLOW,
    name: 'Go to Flow Step',
    defaultAction: () => ({
      category: CATEGORIES.FLOW,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.FLOW_NEXT_STEP,
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.FormSubmit,
    category: CATEGORIES.NATIVE,
    name: 'Submit Form',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.FORM_SUBMIT,
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.URL,
    category: CATEGORIES.NATIVE,
    name: 'Open URL',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.NAVIGATE,
        href: '',
        target: undefined,
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.Callback,
    category: CATEGORIES.NATIVE,
    name: 'Callback',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.CALLBACK,
        name: '',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.TriggerModal,
    category: CATEGORIES.NATIVE,
    name: 'Trigger Overlay',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.TRIGGER_MODAL,
        slug: '',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.TriggerTour,
    category: CATEGORIES.NATIVE,
    name: 'Trigger Tour',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.TRIGGER_TOUR,
        slug: '',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.TriggerHotspotGroup,
    category: CATEGORIES.NATIVE,
    name: 'Trigger Hotspots',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.TRIGGER_HOTSPOT_GROUP,
        slug: '',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.Content,
    category: CATEGORIES.NATIVE,
    name: 'Change Content',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.CHANGE_CONTENT,
        slug: '',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.Email,
    category: CATEGORIES.NATIVE,
    name: 'Send Email',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.NAVIGATE,
        href: 'mailto:',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.TriggerClick,
    category: CATEGORIES.NATIVE,
    name: 'Trigger Click',
    defaultAction: () => ({
      category: CATEGORIES.NATIVE,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.TRIGGER_CLICK,
        selector: '',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
  {
    id: INTERACTION_TYPES.Guide,
    category: CATEGORIES.PENDO,
    name: 'Launch Pendo Guide',
    defaultAction: () => ({
      category: CATEGORIES.PENDO,
      trigger: TRIGGERS.ON_CLICK,
      handler: {
        type: HANDLER_TYPES.OPEN_GUIDE,
        guideId: '',
      },
      eventName: generateEventName(CATEGORIES.NATIVE),
    }),
  },
];
